import React, { useState } from 'react';
import logoUrl from '/src/logo.svg';
import { apiClient } from '../../utils/apiClient';
import './Login.css';

interface LoginProps {
  onLoginSuccess: () => void;
}

interface User {
  id: string;
  name: string;
  employeeNumber: string;
  role:
    | 'SYSTEM_ADMIN'
    | 'ADMIN'
    | 'GENERAL_MANAGER'
    | 'ZONE_MANAGER'
    | 'REPRESENTATIVE'
    | 'SELLER';
  avatarUrl?: string;
}

interface LoginResponse {
  accessToken: string;
  user: User;
}

interface ValidationErrors {
  employeeNumber?: string;
  password?: string;
}

interface TouchedFields {
  employeeNumber: boolean;
  password: boolean;
}

const Login: React.FC<LoginProps> = ({ onLoginSuccess }) => {
  const [employeeNumber, setEmployeeNumber] = useState('');
  const [password, setPassword] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<ValidationErrors>(
    {}
  );
  const [touched, setTouched] = useState<TouchedFields>({
    employeeNumber: false,
    password: false,
  });

  // Validaciones en tiempo real
  const validateEmployeeNumber = (value: string): string | undefined => {
    if (
      touched.employeeNumber &&
      value.trim().length > 0 &&
      value.trim().length < 3
    ) {
      return 'El número de empleado debe tener al menos 3 caracteres';
    }
    return undefined;
  };

  const validatePassword = (value: string): string | undefined => {
    if (
      touched.password &&
      value.trim().length > 0 &&
      value.trim().length < 6
    ) {
      return 'La contraseña debe tener al menos 6 caracteres';
    }
    return undefined;
  };

  // Verificar si el formulario puede ser enviado
  const canSubmit: boolean =
    employeeNumber.trim().length >= 3 &&
    password.trim().length >= 6 &&
    !validationErrors.employeeNumber &&
    !validationErrors.password;

  const handleEmployeeNumberChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ): void => {
    const value = e.target.value;
    setEmployeeNumber(value);

    // Limpiar errores del servidor
    if (errorMsg) setErrorMsg(null);

    // Validar campo
    const error = validateEmployeeNumber(value);
    setValidationErrors((prev): ValidationErrors => {
      const newErrors = { ...prev };
      if (error) {
        newErrors.employeeNumber = error;
      } else {
        delete newErrors.employeeNumber;
      }
      return newErrors;
    });
  };

  const handlePasswordChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ): void => {
    const value = e.target.value;
    setPassword(value);

    // Limpiar errores del servidor
    if (errorMsg) setErrorMsg(null);

    // Validar campo
    const error = validatePassword(value);
    setValidationErrors((prev): ValidationErrors => {
      const newErrors = { ...prev };
      if (error) {
        newErrors.password = error;
      } else {
        delete newErrors.password;
      }
      return newErrors;
    });
  };

  const handleEmployeeNumberBlur = (): void => {
    setTouched((prev): TouchedFields => ({ ...prev, employeeNumber: true }));
    const error = validateEmployeeNumber(employeeNumber);
    setValidationErrors((prev): ValidationErrors => {
      const newErrors = { ...prev };
      if (error) {
        newErrors.employeeNumber = error;
      } else {
        delete newErrors.employeeNumber;
      }
      return newErrors;
    });
  };

  const handlePasswordBlur = (): void => {
    setTouched((prev): TouchedFields => ({ ...prev, password: true }));
    const error = validatePassword(password);
    setValidationErrors((prev): ValidationErrors => {
      const newErrors = { ...prev };
      if (error) {
        newErrors.password = error;
      } else {
        delete newErrors.password;
      }
      return newErrors;
    });
  };

  const handleSubmit: React.FormEventHandler<HTMLFormElement> = (e) => {
    e.preventDefault();

    // Marcar todos los campos como tocados
    setTouched({ employeeNumber: true, password: true });

    // Validar todos los campos antes de enviar
    const employeeError = validateEmployeeNumber(employeeNumber);
    const passwordError = validatePassword(password);

    setValidationErrors((): ValidationErrors => {
      const newErrors: ValidationErrors = {};
      if (employeeError) {
        newErrors.employeeNumber = employeeError;
      }
      if (passwordError) {
        newErrors.password = passwordError;
      }
      return newErrors;
    });

    if (!canSubmit || employeeError || passwordError) return;

    const run = async (): Promise<void> => {
      setIsLoading(true);
      setErrorMsg(null);

      try {
        const data = await apiClient.post<LoginResponse>('/auth/login', {
          employeeNumber,
          password,
        });

        console.log('✅ Autenticación exitosa');

        // Guardar tokens y datos del usuario
        localStorage.setItem('accessToken', data.accessToken);
        localStorage.setItem('user', JSON.stringify(data.user));

        // Notificar éxito
        onLoginSuccess();
      } catch (err) {
        setErrorMsg(
          err instanceof Error ? err.message : 'Error de autenticación'
        );
      } finally {
        setIsLoading(false);
      }
    };

    void run();
  };

  return (
    <div className="login-container bg-primary-dark">
      <span className="fig-1"></span>
      <span className="fig-2"></span>
      <span className="fig-3"></span>
      <span className="fig-4"></span>
      <span className="fig-5"></span>
      <div className="login-card bg-white">
        <div className="login-header">
          <img
            src={logoUrl}
            alt="bomuca"
            className="logo"
            onError={(e): void => {
              (e.currentTarget as HTMLImageElement).style.display = 'none';
            }}
          />
          <h1 className="login-title primary-dark">
            Reporte de proceso
            <br />
            de Venta OTC
          </h1>
        </div>

        <form className="login-form" onSubmit={handleSubmit} noValidate>
          <div className="form-group">
            <label
              htmlFor="employeeNumber"
              className="form-label text-gray-dark"
            >
              Usuario
            </label>
            <input
              id="employeeNumber"
              type="text"
              className={`form-input text-gray-300 ${
                validationErrors.employeeNumber
                  ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
                  : 'border-gray-300'
              }`}
              value={employeeNumber}
              onChange={handleEmployeeNumberChange}
              onBlur={handleEmployeeNumberBlur}
              placeholder="Número de empleado"
              autoComplete="username"
              disabled={isLoading}
            />
            {validationErrors.employeeNumber && (
              <div className="text-red-600 text-sm mt-1">
                {validationErrors.employeeNumber}
              </div>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="password" className="form-label text-gray-dark">
              Contraseña
            </label>
            <div className="password-input-container">
              <input
                id="password"
                type={showPwd ? 'text' : 'password'}
                className={`form-input password-input ${
                  validationErrors.password
                    ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
                    : 'border-gray-300'
                }`}
                value={password}
                onChange={handlePasswordChange}
                onBlur={handlePasswordBlur}
                placeholder="Ingrese su contraseña"
                autoComplete="current-password"
                disabled={isLoading}
              />

              <button
                type="button"
                className="password-toggle text-gray-500"
                aria-label={
                  showPwd ? 'Ocultar contraseña' : 'Mostrar contraseña'
                }
                aria-pressed={showPwd}
                onClick={(): void => {
                  setShowPwd((v: boolean): boolean => !v);
                }}
                disabled={isLoading}
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  {showPwd ? (
                    // eye-off
                    <>
                      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20C5 20 1 12 1 12a18.45 18.45 0 0 1 5.06-5.94" />
                      <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
                      <path d="M14.12 14.12A3 3 0 1 1 9.88 9.88" />
                      <line x1="1" y1="1" x2="23" y2="23" />
                    </>
                  ) : (
                    // eye
                    <>
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                      <circle cx="12" cy="12" r="3" />
                    </>
                  )}
                </svg>
              </button>
            </div>
            {validationErrors.password && (
              <div className="text-red-600 text-sm mt-1">
                {validationErrors.password}
              </div>
            )}
          </div>

          {/* Mensaje de ayuda cuando no se cumplen los requisitos */}
          {!canSubmit && (touched.employeeNumber || touched.password) && (
            <div className="bg-blue-50 border border-blue-200 text-blue-700 px-3 py-2 rounded-lg text-sm mb-4">
              <div className="font-medium mb-1">Requisitos:</div>
              <ul className="text-xs space-y-1">
                <li
                  className={
                    employeeNumber.trim().length >= 3 ? 'text-green-600' : ''
                  }
                >
                  • Usuario: mínimo 3 caracteres
                </li>
                <li
                  className={
                    password.trim().length >= 6 ? 'text-green-600' : ''
                  }
                >
                  • Contraseña: mínimo 6 caracteres
                </li>
              </ul>
            </div>
          )}

          {errorMsg && (
            <div
              style={{
                background: '#FEF2F2',
                border: '1px solid #FECACA',
                color: '#B91C1C',
                padding: '12px',
                borderRadius: 8,
                fontSize: 14,
                marginBottom: 8,
              }}
            >
              {errorMsg}
            </div>
          )}

          <button
            className={`login-button text-white transition-colors ${
              canSubmit
                ? 'bg-primary-dark hover:bg-primary-medium'
                : 'bg-gray-400 cursor-not-allowed'
            }`}
            type="submit"
            disabled={!canSubmit || isLoading}
          >
            {isLoading ? 'Ingresando…' : 'Log in'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default Login;
